import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  ClearLeaderSuggestions,
  GetAsynchronousActivitySchedule,
  GetAttendanceByScheduleSession,
  GetAttendanceByWeeklySchedule,
  GetLeaderSuggestion,
  ReassignLeaderManual,
  ReassignWeekAbsent,
  SetError,
  SetLoading,
  UpdateAsynchronousActivity,
  UpdateAttendance,
} from '@tsin-core/actions/attendance.action';
import {
  AttendanceModel,
  AttendanceStateModel,
  AsyncActivitiesUser,
  LeaderSlotSuggestionModel,
  LoadingAttendanceState,
} from '@tsin-core/models/attendance.model';
import { AttendanceService } from '@tsin-core/services/http/attendance.service';
import { tap, catchError, of } from 'rxjs';

@State<AttendanceStateModel>({
  name: 'attendanceState',
  defaults: {
    loading: LoadingAttendanceState.notLoading,
    attendances: [],
    asycUsers: [],
    error: null,
    leaderSuggestions: [],
    weekAbsentResult: null,
  },
})
@Injectable()
export class AttendanceState {
  attendanceService: AttendanceService = inject(AttendanceService);

  @Selector()
  static getAttendances(state: AttendanceStateModel) {
    return state.attendances;
  }

  @Selector()
  static getAsyncActivitiesUsers(state: AttendanceStateModel) {
    return state.asycUsers;
  }

  @Selector()
  static getLoading(state: AttendanceStateModel) {
    return state.loading;
  }

  @Selector()
  static getError(state: AttendanceStateModel) {
    return state.error;
  }

  @Action(UpdateAttendance)
  updateAttendance(ctx: StateContext<AttendanceStateModel>, action: UpdateAttendance) {
    ctx.patchState({ loading: LoadingAttendanceState.loadingAddUpdate });
    return this.attendanceService.updateAttendanceStatus(action.payload).pipe(
      tap((result: any) => {
        const state = ctx.getState();
        let attendances = [...state.attendances];
        const index = attendances.findIndex(item => item.id === action.payload.id);
        // Merge server result with the existing record so derived fields
        // like `leaderIn` (populated by getAttendanceBySession but not returned
        // by updateAttendanceStatus) are preserved.
        // Fusionner le résultat avec l'enregistrement existant pour préserver les champs
        // dérivés comme `leaderIn` qui ne sont pas renvoyés par updateAttendanceStatus.
        attendances[index] = { ...attendances[index], ...result };
        ctx.patchState({
          attendances: attendances,
          loading: LoadingAttendanceState.notLoading
        })
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingAttendanceState.notLoading,
          error: error.message
        });
        throw (error);
      })
    )
  }


  @Action(UpdateAsynchronousActivity)
  updateAsynchronousActivity(ctx: StateContext<AttendanceStateModel>, action: UpdateAsynchronousActivity) {
    ctx.patchState({ loading: LoadingAttendanceState.loadingAddUpdate });
    return this.attendanceService.updateAsynchronousActivity(action.payload).pipe(
      tap((result: AttendanceModel) => {
        const state = ctx.getState();
        // const index = state.attendances.findIndex(entity => entity?.id === result.id);
        // let attendances = [...state.attendances];
        // attendances[index] = result;
        ctx.patchState({  loading: LoadingAttendanceState.notLoading, error: null });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingAttendanceState.notLoading,
          error: error.message
        });
        throw (error);
      })
    )
  }


  @Action(GetAttendanceByScheduleSession)
  getAttendance(ctx: StateContext<AttendanceStateModel>, action: GetAttendanceByScheduleSession) {
    ctx.patchState({ loading: LoadingAttendanceState.loadingList });

    return this.attendanceService.getAttendanceBySchedule(action.payload).pipe(
      tap((results: AttendanceModel[]) => {
        ctx.patchState({
          attendances: results,
          loading: LoadingAttendanceState.notLoading
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingAttendanceState.notLoading,
          error: error.message,
        });
        throw (error);;
      })
    );
  }

  @Action(GetAttendanceByWeeklySchedule)
  getAttendanceByWeekly(ctx: StateContext<AttendanceStateModel>, action: GetAttendanceByWeeklySchedule) {
    ctx.patchState({ loading: LoadingAttendanceState.loadingList });

    return this.attendanceService.getAttendanceByWeeklySchedule(action.payload).pipe(
      tap((results: AttendanceModel[]) => {
        ctx.patchState({
          attendances: results,
          loading: LoadingAttendanceState.notLoading
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingAttendanceState.notLoading,
          error: error.message,
        });
        throw (error);;
      })
    );
  }


  @Action(GetAsynchronousActivitySchedule)
  getAsynchronousActivityByWeekly(ctx: StateContext<AttendanceStateModel>, action: GetAsynchronousActivitySchedule) {
    ctx.patchState({ loading: LoadingAttendanceState.loadingList });

    return this.attendanceService.getAsynchronousActivityByWeekly(action.payload).pipe(
      tap((results: AsyncActivitiesUser[]) => {
        ctx.patchState({
          asycUsers: results,
          loading: LoadingAttendanceState.notLoading
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingAttendanceState.notLoading,
          error: error.message,
        });
        throw (error);
      })
    );
  }


  @Action(SetLoading)
  setLoading(ctx: StateContext<AttendanceStateModel>, action: SetLoading) {
    ctx.patchState({ loading: action.loading });
  }

  @Action(SetError)
  setError(ctx: StateContext<AttendanceStateModel>, action: SetError) {
    ctx.patchState({ error: action.error });
  }

  // ─── Leader Replacement ─────────────────────────────────────────────────────

  @Selector()
  static getLeaderSuggestions(state: AttendanceStateModel) {
    return state.leaderSuggestions;
  }

  @Selector()
  static getWeekAbsentResult(state: AttendanceStateModel) {
    return state.weekAbsentResult;
  }

  /**
   * Fetch scored replacement candidates for the given session-user's leadership slots.
   * Récupérer les candidats de remplacement pour les créneaux de leadership.
   */
  @Action(GetLeaderSuggestion)
  getLeaderSuggestion(ctx: StateContext<AttendanceStateModel>, action: GetLeaderSuggestion) {
    ctx.patchState({ loading: LoadingAttendanceState.loadingList, leaderSuggestions: [] });
    return this.attendanceService.getLeaderSuggestion(action.sessionUserId).pipe(
      tap((suggestions: LeaderSlotSuggestionModel[]) => {
        ctx.patchState({ leaderSuggestions: suggestions, loading: LoadingAttendanceState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingAttendanceState.notLoading, error: error.message });
        throw error;
      }),
    );
  }

  /**
   * Manually reassign a single leader slot; then refresh the updated record in state.
   * Réassigner manuellement un créneau de leader.
   */
  @Action(ReassignLeaderManual)
  reassignLeaderManual(ctx: StateContext<AttendanceStateModel>, action: ReassignLeaderManual) {
    ctx.patchState({ loading: LoadingAttendanceState.loadingAddUpdate });
    return this.attendanceService.reassignLeader(action.payload).pipe(
      tap(() => {
        ctx.patchState({ loading: LoadingAttendanceState.notLoading, leaderSuggestions: [] });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingAttendanceState.notLoading, error: error.message });
        throw error;
      }),
    );
  }

  /**
   * Auto-reassign all leader slots for a week-absent learner.
   * Réassigner automatiquement tous les créneaux d'un apprenant absent pour la semaine.
   */
  @Action(ReassignWeekAbsent)
  reassignWeekAbsent(ctx: StateContext<AttendanceStateModel>, action: ReassignWeekAbsent) {
    ctx.patchState({ loading: LoadingAttendanceState.loadingAddUpdate, weekAbsentResult: null });
    return this.attendanceService.reassignWeekAbsent(action.payload).pipe(
      tap((result) => {
        ctx.patchState({ weekAbsentResult: result, loading: LoadingAttendanceState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingAttendanceState.notLoading, error: error.message });
        throw error;
      }),
    );
  }

  /** Clear cached suggestions when dialog is closed. */
  @Action(ClearLeaderSuggestions)
  clearLeaderSuggestions(ctx: StateContext<AttendanceStateModel>) {
    ctx.patchState({ leaderSuggestions: [], weekAbsentResult: null });
  }
}
